import React from "react"
import { useEffect } from "react"
import ReactGADist from "react-ga4"
import { usePageContext } from "vike-react/usePageContext"
import PubSub from "pubsub-js"
import { TagManager } from "aldoo-ra/GTMTracker"

// Vite bundles react-ga4 as `export default require_dist()` which returns the
// full CJS exports object { default: GA4Instance, ... } — unwrap it.
const ReactGA = ReactGADist?.default ?? ReactGADist

export default function GoogleTracking({ children }) {
  const { googleAnalyticsId } = usePageContext()

  useEffect(() => {
    if (!googleAnalyticsId) return
    // stage-*/localhost hosts send debug_mode on every hit so events surface
    // in GA4 DebugView without the GA Debugger extension
    const isDebugHost = /^(stage-|localhost$|127\.)/.test(window.location.hostname)
    ReactGA.initialize(
      googleAnalyticsId,
      isDebugHost ? { gtagOptions: { debug_mode: true } } : undefined
    )

    // Page views
    PubSub.subscribe("Tracking_PageView", (_, url) => {
      ReactGA.send({ hitType: "pageview", page: url })
    })

    PubSub.subscribe("Tracking_NewAccountCreated", () => {
      ReactGA.event("ads_conversion_signup")
    })

    // view_item — data: { items, currency, value } — fired when the user
    // lands on a product detail. Required for the GA4 ecommerce funnel
    // (view_item → add_to_cart → begin_checkout → purchase).
    PubSub.subscribe("Tracking_ViewItem", (_, data) => {
      TagManager.trackViewItem(data.items, data.currency)
      ReactGA.event("view_item", {
        currency: data.currency,
        value: data.value,
        items: data.items,
      })
    })

    // view_item_list — data: { items, currency, itemListName? } — list-page
    // impressions (pricing grid). Entry point for users who add to cart
    // without first clicking through a product detail.
    PubSub.subscribe("Tracking_ViewItemList", (_, data) => {
      TagManager.trackViewItemList(data.items, data.currency, data.itemListName)
      ReactGA.event("view_item_list", {
        currency: data.currency,
        items: data.items,
        ...(data.itemListName ? { item_list_name: data.itemListName } : {}),
      })
    })

    // add_to_cart — data: { items, currency, value }
    PubSub.subscribe("Tracking_AddToCart", (_, data) => {
      TagManager.trackAddToCart(data.items, data.currency)
      ReactGA.event("add_to_cart", {
        currency: data.currency,
        value: data.value,
        items: data.items,
      })
    })

    // remove_from_cart — data: { items, currency, value }
    PubSub.subscribe("Tracking_RemoveFromCart", (_, data) => {
      TagManager.trackRemoveFromCart(data.items, data.currency)
      ReactGA.event("remove_from_cart", {
        currency: data.currency,
        value: data.value,
        items: data.items,
      })
    })

    // begin_checkout — data: { items, currency, value, coupon? }
    PubSub.subscribe("Tracking_BeginCheckout", (_, data) => {
      TagManager.trackBeginCheckout(data.items, data.currency, data.coupon)
      ReactGA.event("begin_checkout", {
        currency: data.currency,
        value: data.value,
        items: data.items,
        ...(data.coupon ? { coupon: data.coupon } : {}),
      })
    })

    // purchase — data: { items, currency, value, transactionId, coupon? }
    PubSub.subscribe("Tracking_Purchase", (_, data) => {
      TagManager.trackPurchase({
        transactionId: data.transactionId,
        items: data.items,
        currency: data.currency,
        value: data.value,
        coupon: data.coupon,
      })
      ReactGA.event("purchase", {
        transaction_id: data.transactionId,
        value: data.value,
        currency: data.currency,
        // Landing page / campaign origin from purchase deep links
        ...(data.origin && { purchase_origin: data.origin }),
        items: data.items,
        ...(data.coupon ? { coupon: data.coupon } : {}),
      })
    })
  }, [])

  return children || null
}
